import { useState, useCallback, useRef, useEffect } from 'react';
import { OpenRouterService } from '../services/openRouterService';
import { useLanguage } from './useLanguage';


interface AutocompleteSuggestion {
  suggestion: string;
  position: { start: number; end: number };
  cursorOffset: number;
}

export const useIntelligentAutocomplete = (
  content: string,
  cursorPosition: number,
  openRouterService: OpenRouterService | null,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  onContentUpdate: (newContent: string, newCursorPos: number) => void
) => {
  const [suggestion, setSuggestion] = useState<AutocompleteSuggestion | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useLanguage();
  const lastRequestRef = useRef<string>('');

  // Clear suggestion when cursor moves or content changes
  useEffect(() => {
    if (suggestion && Math.abs(cursorPosition - suggestion.position.end) > 1) {
      setSuggestion(null);
    }
  }, [cursorPosition, suggestion]);

  const getContextForSuggestion = useCallback((text: string, position: number) => {
    const beforeCursor = text.substring(0, position);
    const afterCursor = text.substring(position);
    
    // Get current incomplete word or position for new word
    const wordMatch = beforeCursor.match(/(\S+)$/);
    const currentWord = wordMatch ? wordMatch[0] : '';
    const wordStart = wordMatch ? position - currentWord.length : position;
    
    // Get broader context for better suggestions
    const contextStart = Math.max(0, position - 200);
    const contextEnd = Math.min(text.length, position + 100);
    const fullContext = text.substring(contextStart, contextEnd);
    
    // Get sentence context
    const sentences = beforeCursor.split(/[.!?]+/);
    const currentSentence = sentences[sentences.length - 1]?.trim() || '';
    
    return {
      currentWord,
      wordStart,
      fullContext,
      currentSentence,
      beforeCursor: beforeCursor.substring(Math.max(0, position - 50)),
      afterCursor: afterCursor.substring(0, 50)
    };
  }, []);

  const generateSmartSuggestion = useCallback(async (contextInfo: any): Promise<string | null> => {
    if (!openRouterService) return null;

    const { currentWord, fullContext, currentSentence, beforeCursor } = contextInfo;

    const systemPrompt = language === 'pt-BR' 
      ? `Você é um assistente especializado em completar prompts de IA de forma inteligente.

INSTRUÇÕES PRECISAS:
- Analise o contexto do prompt sendo escrito
- Se há uma palavra incompleta, complete-a de forma natural
- Se o cursor está no final de uma palavra/frase, sugira a próxima palavra ou frase relevante
- Foque em técnicas de prompt engineering, instruções claras, e conceitos de IA
- Retorne APENAS a sugestão de completação, sem explicações
- A sugestão deve fluir naturalmente com o texto existente

IMPORTANTE: Retorne apenas o texto que deve ser inserido no cursor, não repita o que já existe.`

      : `You are an intelligent AI prompt completion assistant.

PRECISE INSTRUCTIONS:
- Analyze the context of the AI prompt being written
- If there's an incomplete word, complete it naturally
- If cursor is at the end of a word/sentence, suggest the next relevant word or phrase
- Focus on prompt engineering techniques, clear instructions, and AI concepts
- Return ONLY the completion suggestion, no explanations
- The suggestion should flow naturally with existing text

IMPORTANT: Return only the text that should be inserted at cursor, don't repeat existing content.`;

    const userPrompt = language === 'pt-BR'
      ? `Contexto do prompt:
"${fullContext}"

Frase atual: "${currentSentence}"
Texto antes do cursor: "${beforeCursor}"
Palavra sendo digitada: "${currentWord}"

Complete de forma inteligente e contextual. Retorne apenas a sugestão de completação:`

      : `Prompt context:
"${fullContext}"

Current sentence: "${currentSentence}"
Text before cursor: "${beforeCursor}"
Word being typed: "${currentWord}"

Complete intelligently and contextually. Return only the completion suggestion:`;

    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openRouterService['config'].apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Aldeia Viva'
        },
        body: JSON.stringify({
          model: openRouterService['config'].model,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          max_tokens: 50,
          temperature: 0.2
        })
      });

      if (!response.ok) return null;

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content?.trim();
      
      return content || null;
    } catch (error) {
      console.warn('Autocomplete API error:', error);
      return null;
    }
  }, [openRouterService, language]);

  const requestSuggestion = useCallback(async () => {
    if (!openRouterService) {
      setSuggestion(null);
      return;
    }

    const contextInfo = getContextForSuggestion(content, cursorPosition);
    const requestKey = `${contextInfo.currentWord}:${cursorPosition}:${contextInfo.beforeCursor.slice(-20)}`;
    
    // Avoid duplicate requests
    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;

    setIsLoading(true);

    try {
      const suggestionText = await generateSmartSuggestion(contextInfo);
      
      if (suggestionText && suggestionText.length > 0) {
        // Clean up the suggestion
        const cleanSuggestion = suggestionText.replace(/^["']|["']$/g, '').trim();
        
        if (cleanSuggestion.length > 0) {
          setSuggestion({
            suggestion: cleanSuggestion,
            position: { start: contextInfo.wordStart, end: cursorPosition },
            cursorOffset: cleanSuggestion.length
          });
        }
      }
    } catch (error) {
      console.warn('Autocomplete error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [content, cursorPosition, openRouterService, getContextForSuggestion, generateSmartSuggestion]);

  // Manual suggestion trigger (called by Ctrl+B)
  const triggerSuggestion = useCallback(() => {
    if (!isLoading) {
      requestSuggestion();
    }
  }, [requestSuggestion, isLoading]);

  const acceptSuggestion = useCallback(() => {
    if (!suggestion) return false;

    const { end } = suggestion.position;
    const newContent = 
      content.substring(0, end) + 
      suggestion.suggestion + 
      content.substring(end);

    // Calculate new cursor position
    const newCursorPos = end + suggestion.suggestion.length;
    
    // Update content and cursor position via callback
    onContentUpdate(newContent, newCursorPos);

    setSuggestion(null);
    return true;
  }, [suggestion, content, onContentUpdate]);

  const rejectSuggestion = useCallback(() => {
    setSuggestion(null);
  }, []);

  return {
    suggestion,
    isLoading,
    triggerSuggestion,
    acceptSuggestion,
    rejectSuggestion
  };
};